import { defineStore } from 'pinia';
import api from '@/js/api.js';
import { useDonorStore } from './DonorStore.js';
import { companyGivingStatus } from '@/helpers/enum.js';

// Define the store
export const useCompanyAdminStore = defineStore('companyAdminStore', {
  state: () => ({
    aboutUsForm: null,
    basicInfoForm: null,
    employeeGivingForm: null,
    featuredCausesForm: null,
    givesDayForm: null,
    mediaForm: null,

    // Generic Company Info
    company: null,
    companyGoal: 0,
    companyLoading: false,
    topCauses: [],
    topCausesLoading: false,
    statistics: null,
    statisticsLoading: false,

    // Company Donations
    totalAmountDonated: 0,
    totalDonations: 0,
    totalEmployees: 0,
    filteredDonations: [],
    recentDonations: [],
    recentDonationsLoading: false,
    allDonations: [],
    allDonationsLoading: false,

    // Company Users
    companyUsers: [],
    companyUsersLoading: false,
    showAddNewUserDialog: false,

    // Resources
    resources: [],
    resourcesLoading: false,

    // Companies collection
    companies: [],

    // Internal request management
    pendingRequest: null,
  }),

  getters: {
    existingManagedProfileByStatus() {
      return (status) => {
        if (!useDonorStore().donorInfo.company_giving_user_profile) {
          return false;
        }
        if (useDonorStore().donorInfo.company_giving_user_profile.inactive === true) {
          return false;
        }
        if (useDonorStore().donorInfo.company_giving_user_profile.manager !== true) {
          return false;
        }
        return useDonorStore().donorInfo.company_giving_user_profile.company_giving.status === status;
      };
    },

    existingPendingApproval() {
      return this.existingManagedProfileByStatus(companyGivingStatus.PENDING_APPROVAL);
    },

    existingPendingProfile() {
      return this.existingManagedProfileByStatus(companyGivingStatus.PENDING_PROFILE);
    },

    existingActiveProfile() {
      return this.existingManagedProfileByStatus(companyGivingStatus.APPROVED);
    },

    noProfile() {
      return (
        !useDonorStore().donorInfo.company_giving_user_profile ||
        useDonorStore().donorInfo.company_giving_user_profile.inactive === true ||
        useDonorStore().donorInfo.company_giving_user_profile.manager === false
      );
    },

    isCompanyLoaded() {
      return this.company !== null;
    },
    oneTimeDonations() {
      return this.donations.filter((donation) => donation.recurringDonation === false);
    },
    recurringDonations() {
      return this.donations.filter((donation) => donation.recurringDonation === true);
    },
    filteredOrganizations() {
      return (searchTerm) => {
        return this.donorOrganizations.filter((org) =>
          org.organizationName.toLowerCase().includes(searchTerm.toLowerCase()),
        );
      };
    },
    filteredCompanies(searchTerm) {
      return (searchTerm) => {
        return this.companies.filter((company) => company.name.toLowerCase().includes(searchTerm.toLowerCase()));
      };
    },
  },

  actions: {
    async getCompany() {
      this.stopPendingRequest();
      this.companyLoading = true;
      try {
        const response = await api.get(`company-giving/authenticated`, { signal: this.pendingRequestSignal() });
        this.company = response.data;
        this.totalDonations = response.data.data;
        this.totalAmountDonated = response.data.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.companyLoading = false;
      }
    },
    async getCompanyCollection() {
      try {
        const companies = [];
        const response = await api.get(`company-giving-collection?hostId=${useDonorStore().hosts_id}`);
        response.data.forEach((company) => {
          if (company.data.name) {
            companies.push(company.data);
          }
        });

        companies.sort((a, b) => a.name.localeCompare(b.name));
        this.companies = companies;
      } catch (e) {
        console.error(e);
      }
    },
    async getRecentDonations() {
      this.recentDonationsLoading = true;
      try {
        const response = await api.get(`company-giving/recent-donations`);
        this.recentDonations = response.data.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.recentDonationsLoading = false;
      }
    },
    async getAllDonations() {
      this.allDonationsLoading = true;
      try {
        const response = await api.get(`company-giving/all-donations`);
        this.allDonations = response.data.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.allDonationsLoading = false;
      }
    },
    async getTopCauses() {
      this.topCausesLoading = true;
      try {
        const response = await api.get(`company-giving/top-causes`);
        this.topCauses = response.data.data;
      } catch (e) {
        console.error(e);
      } finally {
        this.topCausesLoading = false;
      }
    },
    async getStatistics() {
      this.statisticsLoading = true;
      try {
        const response = await api.get(`company-giving/statistics`);
        this.statistics = response.data.data;
        this.totalDonationAmount = response.data.data.total_amount;
        this.totalEmployees = response.data.data.total_employees;
      } catch (e) {
        console.error(e);
      } finally {
        this.statisticsLoading = false;
      }
    },
    async getResources() {
      this.resourcesLoading = true;
      try {
        const response = await api.get(`company-giving/resources`);
        this.resources = response.data.data;
        this.resourcesLoading = false;
      } catch (e) {
        console.error(e);
      } finally {
        this.resourcesLoading = false;
      }
    },
    async checkInvitation(inviteCode, email) {
      return await api.get(`/company-giving/check-invitation?` + new URLSearchParams({ inviteCode, email }));
    },
    async acceptInvitation(inviteCode) {
      return await api.post(`/company-giving/accept-invitation`, { inviteCode });
    },
    async submitBasicInfo() {
      await api.post('/company-giving/submit-enrollment', {
        name: this.basicInfoForm.states.name.value,
        url: this.basicInfoForm.states.url.value,
        customUrl: this.basicInfoForm.states.customUrl.value,
        companyGoal: this.basicInfoForm.states.companyGoal.value,
        aboutUs: this.basicInfoForm.states.aboutUs.value,
        participationText: this.basicInfoForm.states.participationText.value,
        logoImage: this.basicInfoForm.states.logoImage.value,
      });
    },
    async checkUrlAvailability(customUrl, stage) {
      if (!customUrl) {
        return { available: true, message: null }; // Default to available if no customUrl is provided
      }

      const availabilityCheckUrl =
        stage === 'initial'
          ? '/company-giving/check-company-url-availability'
          : '/company-giving/check-url-availability';

      try {
        const response = await api.post(availabilityCheckUrl, {
          customUrl,
          companyGiving_id: this.company?.companyGiving_id,
        });
        return { available: response.data.available, message: response.data.message };
      } catch (e) {
        if (e.response && e.response.data) {
          return { available: false, message: e.response.data.message || 'An unexpected error occurred.' };
        }
        console.error(e);
        return { available: false, message: 'An unexpected error occurred while checking the URL.' };
      }
    },
    // Save all company forms
    async saveProfile() {
      return await this.saveForm([
        this.aboutUsForm,
        this.employeeGivingForm,
        this.featuredCausesForm,
        this.givesDayForm,
        this.mediaForm,
        { status: 6434 },
      ]);
    },
    // Save the contents of one or more company forms
    async saveForm(forms) {
      if (!Array.isArray(forms)) {
        forms = [forms];
      }

      let data = {
        companyGiving_id: this.company.companyGiving_id,
      };

      forms.forEach((form) => {
        if (form && form.states) {
          Object.entries(form.states).forEach(([key, value]) => {
            data[key] = value.value;
          });
        } else if (typeof form == 'object') {
          data = { ...data, ...form };
        }
      });

      await this.patch(data);
    },
    async patch(data) {
      this.stopPendingRequest();
      const response = await api.patch('/company-giving/update', data, { signal: this.pendingRequestSignal() });

      this.company = response.data;

      await this.getCompany();
    },
    async loadCompanyUsers() {
      this.companyUsersLoading = true;
      try {
        const response = await api.get(`/company-giving/users`);
        this.companyUsers = response.data.data;
      } catch (e) {
        console.error(e);
      }
      this.companyUsersLoading = false;
    },

    async addNewUser(email) {
      let response = await api.post('/company-giving/add-user', {
        email,
      });

      this.loadCompanyUsers();

      return response;
    },

    async removeUser(companyGivingUsersProfilesId) {
      let response = await api.delete(`/company-giving/remove-user/${companyGivingUsersProfilesId}`);

      this.loadCompanyUsers();

      return response;
    },

    async resendUserInvite(companyGivingUsersProfilesId) {
      let response = await api.post(`/company-giving/resend-user-invite`, {
        companyGivingUsersProfilesId,
      });

      return response;
    },

    async removeEmployeeDonation(transactions_id) {
      try {
        const response = await api.patch(`/company-giving/remove-employee-donation`, {
          transactions_id,
        });
        return response;
      } catch (e) {
        throw e;
      }
    },
    pendingRequestSignal() {
      // Set up a new pending request signal
      const controller = new AbortController();
      this.pendingRequest = controller;
      return controller.signal;
    },
    stopPendingRequest() {
      // Do we have a pending request?
      if (this.pendingRequest) {
        try {
          // Attempt to stop the request, ignoring any errors
          this.pendingRequest.abort();
        } catch (e) {}
        this.pendingRequest = null;
      }
    },
  },
});
